import { apiClient } from '@/lib/api-client';
import type { VariantAttribute, VariantAttributeValue } from '@/types/product';

/**
 * Tenant library of reusable variant attributes (Size, Colour, …) and their
 * values — `GET/POST /variant-attributes`. Query key: `['variant-attributes']`.
 * There is no delete: deactivating hides an entry from pickers.
 */

export const VARIANT_ATTRIBUTES_QUERY_KEY = ['variant-attributes'] as const;

export interface CreateVariantAttributePayload {
  name: string;
  values?: string[];
  display_order?: number;
}

export interface UpdateVariantAttributePayload {
  name?: string;
  display_order?: number;
  is_active?: boolean;
}

export interface UpdateVariantAttributeValuePayload {
  value?: string;
  display_order?: number;
  is_active?: boolean;
}

/** `/variant-attributes` plus `?include_inactive=true` when asked. Exported for tests. */
export function variantAttributesPath(params?: { includeInactive?: boolean }): string {
  return params?.includeInactive ? '/variant-attributes?include_inactive=true' : '/variant-attributes';
}

export const variantAttributeService = {
  list(params?: { includeInactive?: boolean }) {
    return apiClient<VariantAttribute[]>(variantAttributesPath(params));
  },

  create(payload: CreateVariantAttributePayload) {
    return apiClient<VariantAttribute>('/variant-attributes', { method: 'POST', body: payload });
  },

  update(id: string, payload: UpdateVariantAttributePayload) {
    return apiClient<VariantAttribute>(`/variant-attributes/${id}`, { method: 'PATCH', body: payload });
  },

  addValues(id: string, values: string[]) {
    return apiClient<VariantAttribute>(`/variant-attributes/${id}/values`, {
      method: 'POST',
      body: { values },
    });
  },

  updateValue(id: string, valueId: string, payload: UpdateVariantAttributeValuePayload) {
    return apiClient<VariantAttributeValue>(`/variant-attributes/${id}/values/${valueId}`, {
      method: 'PATCH',
      body: payload,
    });
  },
};
